/**
 * Default game state — extracted from coreSlice.resetGame
 * (11/02/2026 — refactor coreSlice).
 *
 * Returns the canonical "fresh game" payload applied by both the soft
 * reset (`resetGame()`) and the full new-game flow (`startNewGameFresh()`).
 *
 * Defence-in-depth : every cross-slice key the user explicitly added
 * after bug recurrence #4 (27/05/2026) stays listed here so the soft
 * "Quitter" button keeps a clean slate even when new slices are added.
 *
 * Notes :
 *   • `wisdomTipsForNextGame` is intentionally NOT reset (consumed by
 *     createCompany so the player gets Maurice's tips on the next run).
 *   • Caller is responsible for invoking `resetTaxState()` (other slices)
 *     BEFORE applying this payload — see coreSlice.resetGame.
 */
export function buildDefaultGameState() {
  return {
    gameStarted: false, company: null, difficulty: 'normal',
    day: 1, month: 1, year: 1, totalDays: 0,
    treasury: 0, reputation: 50, phase: 1,
    vehicles: [], drivers: [],
    // SCD Lot 1 — reset de la boucle retail.
    retail: null,
    activeContracts: [], availableContracts: [],
    credits: [], activeInvestments: {}, investmentPurchaseDays: {},
    lifetimeCapexSpent: 0,
    activeAids: [], claimedOneShotAids: {}, yearStartSnapshot: null,
    unlockedAchievements: {},
    monthlyRevenues: 0, monthlyExpenses: 0,
    lastDayRevenue: 0, lastDayExpenses: 0,
    pendingEvents: [], eventLog: [],
    postponedEvents: [],
    treasuryHistory: [], alerts: [],
    repHistory: [{ day: 0, month: 1, year: 1, reputation: 50, notoriety: 0 }],
    // SCD Lot 0 — le tutoriel SCD guidait vers les écrans flotte/contrats
    // supprimés : désactivé (tutorialComplete: true). Un onboarding retail
    // sera écrit dans un lot ultérieur.
    tutorialStep: 0, tutorialComplete: true,
    contextualTutorialsSeen: {}, activeContextualTuto: null,
    pendingPhaseUp: null,
    pendingMarchettiBonus: null,
    pendingBoardMeeting: null,
    pendingMidYearReview: null,
    // 01/06/2026 — Tender / AO subsystem reset.
    activeTenders: [],
    activeApplications: [],
    tenderHistory: [],
    drealFineCooldowns: {},
    deferredMessages: [],
    notifications: [],
    achievements: {},
    pendingMonthlyReport: null,
    pendingAnnualReport: null,
    monthlyHistory: [],
    yearlyReports: [],
    activeResolution: null,
    activeChallenge: null,
    pendingAudit: null,
    pendingVehicleFinancing: null,
    marginPressureActive: false,
    auditHistory: [],
    auditsThisYear: 0,
    lastAuditYear: 0,
    attritionHistory: [],
    firedDriversArchive: {},
    // 24/07/2026 — registre du garde-fou anti-disparition (rosterGuardTick).
    _rosterLedger: {},
    // 01/06/2026 — Annual EcoVadis-style audit history reset.
    ecoVadisHistory: [],
    // 31/05/2026 — breakdown counter (support yearly digest)
    breakdownsThisYear: 0,
    // 17/06/2026 (Lot 4) — annual counters reset
    transfersInitiatedThisYear: 0,
    holidaysGrantedThisYear: 0,
    holidayDriversWentOnLeaveThisYear: 0,
    vehiclesScrappedThisYear: 0,
    fuelTheftsResolvedThisYear: 0,
    inbox: [],
    lastRandomMessageDay: 0,
    lastHolidayQuarterlyReportDay: 0,
    completedChallenges: {},
    selectedAdvisor: null,
    advisorEnabled: true,
    // Per-client argumentation defaults reset on full game reset.
    clientArgumentationDefaults: {},
    // Bank slice
    bankRelations: {
      bnb_mariba: { loansRepaid: 0, totalBorrowed: 0, activeLoanCount: 0, firstLoanDay: null, loyaltyLevel: 0 },
      societe_generaliste: { loansRepaid: 0, totalBorrowed: 0, activeLoanCount: 0, firstLoanDay: null, loyaltyLevel: 0 },
      credit_agricool: { loansRepaid: 0, totalBorrowed: 0, activeLoanCount: 0, firstLoanDay: null, loyaltyLevel: 0 },
    },
    activePlacements: [],
    pendingProposals: null,
    creditScore: null,
    scoreRevealed: false,
    scoreRevealMessage: null,
    scoreRevealDismissed: false,
    // Support slice
    hiredSupport: {},
    tradeShowMonth: 0,
    // 05/02/2026 (Q1 Lot 2) — Marketing salon arc.
    marketingSalon: null,
    pendingSalonInvitation: null,
    lastSalonProposalDay: 0,
    salonProspectTenders: 0,
    // 05/02/2026 (Q1 engagement) — Sound toggle for juicy AO result
    // notifications. Default false (respects users playing in a meeting).
    aoSoundEnabled: false,
    // Bankruptcy / GameOver
    insolvencyStreak: 0,
    bankruptcyAlertStage: null,
    bankruptcyAlertLastPushDay: 0,
    emergencyShareholderUsed: false,
    pendingShareholderMeeting: null,
    personalCapitalInjected: 0,
    fuelPriceModifier: null,
    peageStrike: null,
    gameOverState: null,
    // Acquisitions / multi-depot
    ownedDepots: [],
    activeDepotId: null,
    pendingAcquisition: null,
    acquiredCompetitorIds: [],
    // 12/02/2026 — Seuils narratifs marketScale (presse) + fenêtre agressive.
    marketScaleFiredThresholds: [],
    competitorAggressionUntilDay: 0,
    lastAcquisitionSummary: null,
    loiRefusalCooldown: {},
    // CRM
    clientLoyalty: {},
    clientActionCooldowns: {},
    // Strike modifier
    companyStrike: null,
    marketBoom: null,
    // Nav "NEW" badges cleared on full reset.
    newFeatureCounts: {},
    milestonesSeen: [],
    lastMilestoneEvent: null,
    seenFirstRenegoFailure: false,
    // ────────────── Defence-in-depth: clear EVERY known cross-slice state ──────
    // The user reported (27/05/2026) that residual state from a previous run
    // sometimes leaked into a new one (bug recurrence #4). The cleanest
    // remedy is `startNewGameFresh()` (full reload via saveStore) but this
    // soft-reset path also explicitly clears every known cross-slice key
    // so the soft "Quitter" button stays safe even if a new slice is added
    // later without being remembered here.
    manufacturerRelations: {},
    pendingMfgTradeShow: null,
    pendingFiscalQuiz: null,
    pendingCompetitionEvent: null,
    pendingCharter: null,
    clientRoster: null,
    driverNegotiations: {},
    pedagogicalObjectives: [],
    // Office reset (28/05/2026 v2)
    officeUnlockedAt: null,
    officeUpgrades: {},
    nextOfficeProposalDay: null,
    // 18/06/2026 — compte à rebours siège social.
    hqDeadlineDay: null,
    hqRemindersSent: {},
    hqResignationHappened: false,
    // 17/06/2026 — Refonte dépôts + opportunités commerciales : reset
    // pour éviter les leaks d'état entre tests Jest.
    hiredDepotManagers: {},
    commercialOpportunities: [],
    hideUnprofitableEmailSent: false,
    fiscalBreakdownEmailSent: false,
    // 12/02/2026 (fix user prod « Il est arrivé de récupérer la flotte
    // de chauffeurs au cours de la nouvelle partie ») — Reset explicite
    // du vivier RH (driverPool + events) qui manquait auparavant et
    // faisait fuiter les 6 candidats (dont ceux ajoutés par Nadia/Karim)
    // entre parties. Sans ce reset, appuyer sur "Nouvelle partie" sans
    // recharger la page laissait les anciens candidats dans le pool.
    driverPool: {
      availableIds: [],
      takenBy: {},
      lastTickDay: 0,
      lastJobFairDay: -999,
      jobAdActive: false,
      jobAdEndDay: 0,
    },
    driverPoolEvents: [],
    // Note: wisdomTipsForNextGame is NOT reset here — it is consumed by
    // createCompany on the very next game so the player gets Maurice's tips.
  };
}
